import { Link } from "react-router-dom";
import {
  ArrowLeft, MessageCircle, Phone, MapPin, Star, BadgeCheck, ArrowRight, Tag,
} from "lucide-react";
import { waLink, BRAND } from "@/data/site";
import { crop } from "@/data/industries";

const DemoWebsite = ({ demo, slug }) => {
  const accent = demo.accent;
  const wa = waLink(demo.whatsappMessage);
  const mapsUrl = `https://www.google.com/maps/search/${encodeURIComponent(demo.business)}`;

  return (
    <div className="min-h-screen bg-[#FDFBF7]" data-testid={`demo-${slug}`}>
      {/* Demo label bar */}
      <div className="sticky top-0 z-50 bg-[#3E2723] text-white" data-testid="demo-label-bar">
        <div className="container-site flex h-11 items-center justify-between gap-3 text-xs sm:text-sm">
          <p className="flex items-center gap-2 font-semibold">
            <span className="rounded bg-[#A88667] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">Demo Website</span>
            <span className="hidden sm:inline text-[#E7DED2]/80">This is an example website created for this industry.</span>
          </p>
          <Link to="/" data-testid="demo-back-link" className="flex items-center gap-1.5 font-semibold text-[#D7B594] hover:text-white transition-colors duration-200">
            <ArrowLeft className="h-4 w-4" /> Back to Disign Digital
          </Link>
        </div>
      </div>

      {/* Demo nav */}
      <div className="border-b border-[#EFEBE4] bg-white/90 backdrop-blur-xl">
        <div className="container-site flex h-16 items-center justify-between">
          <p className="font-heading text-lg sm:text-xl font-bold" style={{ color: accent }} data-testid="demo-business-name">
            {demo.business}
          </p>
          <nav className="hidden md:flex items-center gap-5">
            {demo.nav.map((n) => (
              <a key={n} href={`#demo-${n.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="text-sm font-medium text-[#5D4037] hover:text-[#3E2723] transition-colors duration-200">
                {n}
              </a>
            ))}
          </nav>
          <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="demo-nav-cta" className="btn-whatsapp !py-2 !px-4 text-xs sm:text-sm">
            <MessageCircle className="h-4 w-4" /> {demo.primaryCta}
          </a>
        </div>
      </div>

      {/* Demo hero */}
      <section className="relative">
        <div className="container-site grid items-center gap-10 py-14 sm:py-20 lg:grid-cols-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.18em]" style={{ color: accent }}>{demo.industry}</p>
            <h1 className="mt-3 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold leading-tight text-[#3E2723]" data-testid="demo-headline">
              {demo.headline}
            </h1>
            <p className="mt-4 max-w-lg text-base sm:text-lg text-[#5D4037]">{demo.subline}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="demo-hero-whatsapp" className="btn-whatsapp">
                <MessageCircle className="h-4 w-4" /> {demo.primaryCta}
              </a>
              {demo.secondaryCtas.map((c) =>
                c === "Get Directions" ? (
                  <a key={c} href={mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="demo-hero-directions" className="btn-outline">
                    <MapPin className="h-4 w-4" /> {c}
                  </a>
                ) : c === "Call Now" ? (
                  <a key={c} href="#demo-contact" data-testid="demo-hero-call" className="btn-outline">
                    <Phone className="h-4 w-4" /> {c}
                  </a>
                ) : (
                  <a key={c} href="#demo-services" data-testid={`demo-hero-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="btn-outline">
                    {c}
                  </a>
                )
              )}
            </div>
          </div>
          <div className="overflow-hidden rounded-3xl border border-[#EFEBE4] shadow-md">
            <img src={demo.image} alt={`${demo.business} — ${demo.industry} demo website`} className="h-64 sm:h-80 lg:h-96 w-full object-cover" />
          </div>
        </div>
      </section>

      {/* Demo services */}
      <section id="demo-services" className="bg-[#F5F2EB] py-14 sm:py-20">
        <div className="container-site">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#3E2723]" data-testid="demo-services-title">{demo.servicesTitle}</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {demo.services.map((s) => (
              <div key={s.title} className="rounded-2xl border border-[#EFEBE4] bg-white p-6 shadow-sm transition-shadow duration-200 hover:shadow-md">
                <div className="h-1.5 w-10 rounded-full" style={{ background: accent }} />
                <h3 className="mt-4 font-heading text-base sm:text-lg font-bold text-[#3E2723]">{s.title}</h3>
                <p className="mt-1.5 text-sm text-[#5D4037]">{s.desc}</p>
              </div>
            ))}
          </div>

          {demo.offers && (
            <div id="demo-offers" className="mt-10 rounded-2xl border border-dashed p-6" style={{ borderColor: accent }} data-testid="demo-offers">
              <p className="flex items-center gap-2 font-heading text-base font-bold" style={{ color: accent }}>
                <Tag className="h-4 w-4" /> Current Offers
              </p>
              <ul className="mt-3 grid gap-2 sm:grid-cols-3">
                {demo.offers.map((o) => (
                  <li key={o} className="rounded-xl bg-white px-4 py-3 text-sm font-medium text-[#3E2723] shadow-sm">{o}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </section>

      {/* Demo gallery */}
      <section id="demo-gallery" className="py-14 sm:py-20">
        <div className="container-site">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#3E2723]">Gallery</h2>
          <div className="mt-8 grid grid-cols-2 gap-4 sm:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <img
                key={i}
                src={crop(demo.image, 600 + i * 40, 420 + i * 30)}
                alt={`${demo.business} gallery photo ${i + 1}`}
                className="h-40 sm:h-56 w-full rounded-2xl border border-[#EFEBE4] object-cover shadow-sm"
                loading="lazy"
              />
            ))}
          </div>
        </div>
      </section>

      {/* Demo reviews placeholder */}
      <section id="demo-reviews" className="bg-[#F5F2EB] py-14 sm:py-20">
        <div className="container-site">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#3E2723]">Customer Reviews</h2>
          <div className="mt-8 grid gap-5 sm:grid-cols-3" data-testid="demo-reviews-placeholder">
            {[1, 2, 3].map((i) => (
              <div key={i} className="rounded-2xl border border-dashed border-[#D7B594] bg-white/70 p-6 text-center">
                <div className="flex justify-center gap-1">
                  {[...Array(5)].map((_, s) => (
                    <Star key={s} className="h-4 w-4 text-[#D7B594]" />
                  ))}
                </div>
                <p className="mt-3 text-sm text-[#8D6E63]">
                  Your Google reviews and customer testimonials would appear here.
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Demo contact */}
      <section id="demo-contact" className="py-14 sm:py-20">
        <div className="container-site">
          <div className="grid gap-8 lg:grid-cols-2">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold text-[#3E2723]">Visit or Contact Us</h2>
              <p className="mt-3 text-sm sm:text-base text-[#5D4037]">{demo.contactNote}</p>
              <div className="mt-6 flex flex-wrap gap-3">
                <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="demo-contact-whatsapp" className="btn-whatsapp">
                  <MessageCircle className="h-4 w-4" /> {demo.primaryCta}
                </a>
                <a href={mapsUrl} target="_blank" rel="noopener noreferrer" className="btn-outline">
                  <MapPin className="h-4 w-4" /> Get Directions
                </a>
                <a href="#demo-contact" className="btn-outline">
                  <Phone className="h-4 w-4" /> Call Now
                </a>
              </div>
            </div>
            <div className="flex min-h-56 items-center justify-center rounded-2xl border border-dashed border-[#D7B594] bg-[#F5F2EB] p-8 text-center">
              <div>
                <MapPin className="mx-auto h-8 w-8 text-[#A88667]" />
                <p className="mt-3 max-w-xs text-sm text-[#8D6E63]">
                  Your Google Maps location would be embedded here, so customers can navigate to your business with one tap.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Conversion band — Disign Digital */}
      <section className="bg-[#3E2723] py-14 sm:py-16" data-testid="demo-conversion-band">
        <div className="container-site text-center">
          <BadgeCheck className="mx-auto h-9 w-9 text-[#D7B594]" />
          <h2 className="mt-4 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold text-[#F5F2EB]">
            Want a Website Like This for Your Business?
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-sm sm:text-base text-[#E7DED2]/80">
            This is a demo by {BRAND.name}. We can build one specifically for your {demo.industry.toLowerCase()} business — with your name, your photos and your contact buttons.
          </p>
          <div className="mt-7 flex flex-wrap justify-center gap-4">
            <a href={wa} target="_blank" rel="noopener noreferrer" data-testid="demo-cta-whatsapp" className="btn-whatsapp">
              <MessageCircle className="h-4 w-4" /> WhatsApp Disign Digital
            </a>
            <Link to="/#pricing" data-testid="demo-cta-pricing" className="btn-outline !border-[#D7B594] !text-[#F5F2EB] hover:!bg-white/10">
              See Pricing <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      <div className="border-t border-[#EFEBE4] bg-white py-5 text-center text-xs text-[#8D6E63]">
        {demo.business} is a demo website created by {BRAND.name} to show what your business website could look like.
      </div>
    </div>
  );
};

export default DemoWebsite;
